/**
 * UserContextProvider Hook - Orchestrates mediator + store
 */

import { useEffect } from 'react';
import { logger, mediator } from '@/application/core';
import { GetUserContextQuery, type UserContextData } from '@/application/queries/userContext';
import {
  useUserContextData,
  useUserContextLoading,
  useUserContextError,
  useSetUserContextData,
  useSetUserContextLoading,
  useSetUserContextError,
} from './UserContextProvider.store';

export function useUserContext() {
  const data = useUserContextData();
  const loading = useUserContextLoading();
  const error = useUserContextError();
  const setData = useSetUserContextData();
  const setLoading = useSetUserContextLoading();
  const setError = useSetUserContextError();

  useEffect(() => {
    let mounted = true;

    async function fetchUserContext() {
      if (data) {
        logger.debug('useUserContext', 'Data already exists, skipping fetch');
        return;
      }

      logger.debug('useUserContext', 'Starting fetchUserContext...');
      setLoading(true);
      setError(null);
      try {
        logger.debug('useUserContext', 'Sending GetUserContextQuery...');
        const result = await mediator.send<UserContextData>(new GetUserContextQuery());
        logger.debug('useUserContext', 'Query result:', result);
        if (mounted) {
          setData(result);
          setLoading(false);
        }
      } catch (err) {
        logger.error('useUserContext', 'Error:', err);
        if (mounted) {
          setError(err instanceof Error ? err : new Error('Failed to fetch user context'));
          setLoading(false);
        }
      }
    }

    fetchUserContext();

    return () => {
      mounted = false;
    };
  }, []); // Run once on mount

  return { data, loading, error };
}
